<template>
  <div>
    <v-dialog
      fullscreen
      scrollable
      transition="dialog-bottom-transition"
      v-model="dialog">
      <v-card>
        <v-card-title v-if="selected">
          {{ selected.created_at | date }}
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-img v-if="selected" :src="selected.url" aspect-ratio="1" contain></v-img>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-row>
      <v-col
        cols="4"
        v-for="(image, index) in images"
        :key="`img_${index}`"
      >
        <v-card text tile class="image-card">
          <v-img :src="image.url" aspect-ratio="1" @click="selectImage(image)"></v-img>
          <v-btn
            absolute
            dark
            fab
            small
            color="secondary"
            class="delete-button"
            @click="removeImage(image)"
            v-if="!readonly"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-card>
      </v-col>
      <v-col
        v-for="(dataUri, index) in uploadImages"
        :key="`uri_${index}`"
        cols="4"
        lg="2"
        xl="1"
      >
        <v-card text tile class="image-card offline">
          <v-img
            :src="dataUri"
            aspect-ratio="1"
            @click="selectImageUri(dataUri)">
          </v-img>
          <v-icon
            color="white"
            class="cloud-icon"
            x-large
          >
            mdi-cloud-off-outline
          </v-icon>
          <v-btn
            absolute
            dark
            fab
            small
            color="secondary"
            @click="removeUploadImage(index)"
            v-if="!readonly"
            class="delete-button"
          >
              <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-card>
      </v-col>
      <v-col cols="12" v-if="isLoading">
        <v-card flat class="text-center">
          {{ $t('讀取中') }}
          <v-card-text>
            <v-progress-linear
              indeterminate
              rounded
              height="6"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" class="pb-0" v-else-if="canUpload">
        <slot></slot>
        <v-btn
          block
          depressed
          class="ma-0 add-button"
          @click="onFocus"
        >
          {{ $t('上傳照片') }} <v-icon>mdi-image-plus</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <input
      type="file"
      :accept="inputAccept"
      multiple
      ref="fileInput"
      :capture="inputCapture"
      @change="onFileChange"
    >
  </div>
</template>

<script>
import { date } from '../../../libs/filters.js';
import readAndCompressImage from '../../../libs/read-and-compress-image';

export default {
  props: {
    images: {
      type: Array,
    },

    accept: {
      type: String,
      default: 'image/png,image/gif,image/jpeg'
    },

    maxSize: {
      type: Number,
      default: 8 // megabytes
    },

    max: {
      type: Number,
      default: 10 // files
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    cameraOnly: {
      type: Boolean,
      default: false,
    },

    loading: Boolean,
  },

  filters: {
    date,
  },

  data () {
    return {
      isLoading: false,
      uploadImages: [],
      selected: null,
      dialog: false,
      files: [],
      isStagingSite: process.env.MIX_STAGING_SITE === 'true',
    }
  },

  computed: {
    canUpload() {
      if (this.readonly) {
        return false;
      }

      return this.max - this.uploadImages.length > 0;
    },

    inputAccept() {
      if (this.cameraOnly) {
        return 'image/*';
      }

      return this.isStagingSite ? this.accept : `${this.accept},android/allowCamera`;
    },

    inputCapture() {
      if (this.cameraOnly) {
        return 'environment';
      }

      return this.isStagingSite ? 'environment' : undefined;
    }
  },

  watch: {
    isLoading() {
      this.$emit('update:loading', this.isLoading);
    },
  },

  methods: {
    onFocus () {
      this.$refs.fileInput.click();
    },

    async onFileChange ($event) {
      const files = $event.target.files || $event.dataTransfer.files;
      if (!files) {
        return;
      }

      const availableCount = this.max - this.uploadImages.length;
      if (files.length > availableCount) {
        alert(`Exceeded the max files. Only ${this.max} files accepted.`);
        return;
      }

      this.isLoading = true;
      const validatedFiles = [...files].filter(file => this.validateImageSize(file));
      for (let file of validatedFiles) {
        const imageBlob = await readAndCompressImage(file);
        const uri = URL.createObjectURL(imageBlob);
        this.uploadImages.push(uri);
        this.files.push(imageBlob);
      }

      this.isLoading = false;

      this.$emit('change', this.files);
      this.$refs.fileInput.value = null;
    },

    validateImageSize(file) {
      const size = file.size && (file.size / Math.pow(1000, 2));

      if (size > this.maxSize) {
        alert(`The image you're trying to upload is too large. The max image file size is set to ${this.maxSize}Mb.\n${file.name}: ${size}Mb`);
        return false;
      }

      return true;
    },

    removeImage(image) {
      this.$emit('delete', image);
    },

    removeUploadImage(index) {
      if (!confirm(this.$t("確定刪除照片嗎？"))) {
        return ;
      }

      this.uploadImages.splice(index, 1);

      this.files.splice(index, 1);
      this.$emit('change', this.files);
    },

    selectImage(image) {
      this.selected = image;
      this.dialog = true;
    },

    selectImageUri(imageUri) {
      this.selected = {
        url: imageUri,
        created_at: new Date(),
      };
      this.dialog = true;
    },

    clear() {
      this.uploadImages = [];
      this.files = [];
      this.$emit('change', this.files);
    },
  },

}
</script>

<style lang="scss" scoped>
.add-button {
  min-height: 60px;
}

.image-card {
  position: relative;

  &.offline {
    opacity: 0.4;
  }

  .delete-button {
    right: -10px;
    top: -10px;
  }

  .cloud-icon {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}

input[type=file] {
  position: absolute;
  left: -99999px;
}
</style>
